import React from 'react'
import { useQuery } from '@apollo/react-hooks'
import { gql } from 'apollo-boost'
import { useParams } from 'react-router'
import { SubscriptionQuery, SubscriptionQueryVariables } from './__generated__/SubscriptionQuery'
import { Button, Card, CircularProgress, Table, TableCell, TableRow, Typography, makeStyles } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { Spacer } from '@vestaboard/installables'
import { BugReport, Check, CheckBoxOutlineBlank, Timeline } from '@material-ui/icons'
import { FormattedDateTime } from '../formatted-date-time'
import { subscriptionMessageAppearanceActionAnalyticsPath } from '../../subscriptionPaths'
import { TruncatedId } from '../truncated-id'
import { LARAVEL_ENDPOINT } from '../../env'

const INSTALLABLES_SERVER_LOGS_BASE = `${LARAVEL_ENDPOINT}/admin/errors/subscription`

const QUERY = gql`
  query SubscriptionQuery($id: String!) {
    subscription(id: $id) {
      id
      title
      icon
      description
      muted
      rank
      created
      subscriptionSet {
        id
        boards {
          id
          title
        }
      }
      installation {
        id
        installable {
          id
          title
        }
      }
      configurationUrl
      configurationToken
      runOfShow {
        description
      }
    }
  }
`

const useStyles = makeStyles({
  columnTitle: {
    width: '200px',
    fontWeight: 'bold',
    background: '#eee'
  },
  toolDescription: {
    display: 'block',
    marginTop: 4,
    color: '#666'
  }
})

export const Subscription = () => {
  const { id } = useParams<{ id: string }>()
  const { data, loading, error } = useQuery<SubscriptionQuery, SubscriptionQueryVariables>(QUERY, { variables: { id } })
  const classes = useStyles()

  if (loading) {
    return <CircularProgress />
  }

  if (error) {
    return <Alert severity='error'>Subscription not found.</Alert>
  }

  return (
    <>
      <Typography variant='h4'>Subscription</Typography>
      <Spacer size='extraLarge' />
      <Card>
        <Table>
          <TableRow>
            <TableCell className={classes.columnTitle}>ID</TableCell>
            <TableCell>{data?.subscription.id}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.columnTitle}>Title</TableCell>
            <TableCell>{data?.subscription.title || 'No Subscription Title'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.columnTitle}>Description</TableCell>
            <TableCell>{data?.subscription.description || 'No Subscription Description'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.columnTitle}>Created</TableCell>
            <TableCell>
              <FormattedDateTime value={data?.subscription.created ? +data?.subscription.created : undefined} />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.columnTitle}>Muted?</TableCell>
            <TableCell>{data?.subscription.muted ? <Check /> : <CheckBoxOutlineBlank />}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.columnTitle}>Run of Show Description</TableCell>
            <TableCell>{data?.subscription.runOfShow.description || 'No Subscription Description'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.columnTitle}>Installable</TableCell>
            <TableCell>
              {data?.subscription.installation?.installable.id ? (
                <TruncatedId
                  value={data?.subscription.installation?.installable.id || ''}
                  path={`/installables/${data?.subscription.installation?.installable.id}`}
                  full
                />
              ) : (
                'No Installable'
              )}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.columnTitle}>Installation</TableCell>
            <TableCell>
              {data?.subscription.installation ? (
                <TruncatedId
                  key={data?.subscription.installation.id}
                  value={data?.subscription.installation.id || ''}
                  path={`/installation/${data?.subscription.installation.id}`}
                  full
                />
              ) : (
                'No Installation'
              )}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.columnTitle}>Boards</TableCell>
            <TableCell>
              {(data?.subscription.subscriptionSet.boards || []).map(board => (
                <TruncatedId key={board.id} value={board.title || ''} path={`/boards/${board.id}`} />
              ))}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.columnTitle}>Laravel Logs</TableCell>
            <TableCell>
              <Button
                component='a'
                href={`${INSTALLABLES_SERVER_LOGS_BASE}/${id}`}
                target='_blank'
                rel='noopener noreferrer'
                color='primary'
                variant='contained'
                size='small'
                startIcon={<BugReport fontSize='small' />}>
                Open Laravel logs
              </Button>
              <Typography variant='caption' className={classes.toolDescription}>
                Central scheduler info (subset)
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.columnTitle}>Message Event History</TableCell>
            <TableCell>
              <Button
                component='a'
                href={subscriptionMessageAppearanceActionAnalyticsPath(id)}
                target='_blank'
                rel='noopener noreferrer'
                color='primary'
                variant='contained'
                size='small'
                startIcon={<Timeline fontSize='small' />}>
                Message event history
              </Button>
              <Typography variant='caption' className={classes.toolDescription}>
                Post-installable message tracking (rolling 5 days)
              </Typography>
            </TableCell>
          </TableRow>
        </Table>
      </Card>
    </>
  )
}
